import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
    TruckIcon, 
    MapPinIcon, 
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';

const statusTranslations = {
    'created': 'Kargo Oluşturuldu',
    'draft': 'Hazırlanıyor',
    'ready': 'İşleme Hazır',
    'processing': 'İşleniyor',
    'confirmed': 'Onaylandı',
    'shipped': 'Kargoya Verildi',
    'in_transit': 'Yolda',
    'delivered': 'Teslim Edildi',
    'cancelled': 'İptal Edildi',
    'shipping_failed': 'Kargo Hatası',
    'delivery_failed': 'Teslim Başarısız',
    'cancellation_requested': 'İptal Talebi',
    'lost': 'Kayıp',
    'returned': 'İade Edildi'
};

const statusColors = {
    'created': 'bg-blue-100 text-blue-800 border-blue-200',
    'draft': 'bg-gray-100 text-gray-800 border-gray-200',
    'ready': 'bg-cyan-100 text-cyan-800 border-cyan-200',
    'processing': 'bg-blue-200 text-blue-900 border-blue-300',
    'confirmed': 'bg-green-100 text-green-800 border-green-200',
    'shipped': 'bg-purple-100 text-purple-800 border-purple-200',
    'in_transit': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'delivered': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'cancelled': 'bg-red-100 text-red-800 border-red-200',
    'shipping_failed': 'bg-red-200 text-red-900 border-red-300',
    'delivery_failed': 'bg-orange-200 text-orange-900 border-orange-300',
    'cancellation_requested': 'bg-yellow-200 text-yellow-900 border-yellow-300',
    'lost': 'bg-gray-200 text-gray-900 border-gray-300',
    'returned': 'bg-orange-100 text-orange-800 border-orange-200'
};

const statusIcons = {
    'created': ClockIcon,
    'draft': ClockIcon,
    'ready': ClockIcon,
    'processing': ClockIcon,
    'confirmed': CheckCircleIcon,
    'shipped': TruckIcon,
    'in_transit': TruckIcon,
    'delivered': CheckCircleIcon,
    'cancelled': ExclamationTriangleIcon,
    'shipping_failed': ExclamationTriangleIcon,
    'delivery_failed': ExclamationTriangleIcon,
    'cancellation_requested': ExclamationTriangleIcon,
    'lost': ExclamationTriangleIcon,
    'returned': ExclamationTriangleIcon
};

function TrackingPage() {
    const { trackingCode: urlTrackingCode } = useParams();
    const navigate = useNavigate();
    const [trackingCode, setTrackingCode] = useState(urlTrackingCode || '');
    const [shipmentData, setShipmentData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);

    useEffect(() => {
        if (urlTrackingCode) {
            handleTrackShipment(urlTrackingCode);
        }
    }, [urlTrackingCode]);

    const handleTrackShipment = async (code = trackingCode) => {
        if (!code.trim()) {
            toast.error('Lütfen takip numarası girin');
            return;
        }

        setLoading(true);
        setSearched(true);
        
        try {
            const response = await api.get(`/api/shipping/track/${code.trim()}`);
            setShipmentData(response.data);
            
            // URL'yi güncelle
            if (code !== urlTrackingCode) {
                navigate(`/kargo-takip/${code}`, { replace: true });
            }
        } catch (error) {
            console.error('Kargo takip hatası:', error);
            if (error.response?.status === 404) {
                toast.error('Takip numarası bulunamadı');
            } else {
                toast.error('Kargo takibi yapılırken bir hata oluştu');
            }
            setShipmentData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleTrackShipment();
        }
    };

    const getStatusIcon = (status) => {
        const IconComponent = statusIcons[status] || ClockIcon;
        return <IconComponent className="h-6 w-6" />;
    };

    const getProgressPercentage = (status) => {
        const statusOrder = ['created', 'draft', 'confirmed', 'shipped', 'in_transit', 'delivered'];
        const currentIndex = statusOrder.indexOf(status);
        if (currentIndex === -1) return 0;
        return ((currentIndex + 1) / statusOrder.length) * 100;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green rounded-full mb-4 shadow-lg">
                        <TruckIcon className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Kargo Takip</h1>
                    <p className="text-gray-600 text-lg">Siparişinizin nerede olduğunu öğrenin</p>
                </div>

                {/* Takip Formu */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mb-8">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                placeholder="Takip numaranızı girin..."
                                value={trackingCode}
                                onChange={(e) => setTrackingCode(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green text-lg"
                            />
                            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        </div>
                        <button
                            onClick={() => handleTrackShipment()}
                            disabled={loading}
                            className="px-8 py-3 bg-brand-green text-white rounded-xl hover:bg-brand-green-dark transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Aranıyor...' : 'Takip Et'}
                        </button>
                    </div>
                </div>

                {/* Sonuçlar */}
                {loading && (
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-green border-t-transparent mx-auto mb-4"></div>
                        <p className="text-gray-600">Kargo bilgileri getiriliyor...</p>
                    </div>
                )}

                {!loading && searched && !shipmentData && (
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
                        <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Kargo Bulunamadı</h3>
                        <p className="text-gray-600">
                            Girdiğiniz takip numarası ile ilgili bir kargo kaydı bulunamadı. 
                            Lütfen takip numaranızı kontrol edin.
                        </p>
                    </div>
                )}

                {shipmentData && (
                    <div className="space-y-6">
                        {/* Kargo Durumu */}
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                            <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-6 text-white">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-2xl font-bold mb-1">
                                            Sipariş #{shipmentData.order_number}
                                        </h2>
                                        <p className="opacity-90">
                                            Takip No: {shipmentData.tracking_code}
                                        </p>
                                    </div>
                                    <div className={`px-4 py-2 rounded-full border-2 ${statusColors[shipmentData.status]} bg-white`}>
                                        <div className="flex items-center">
                                            {getStatusIcon(shipmentData.status)}
                                            <span className="ml-2 font-semibold">
                                                {statusTranslations[shipmentData.status]}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="p-6">
                                <div className="mb-4">
                                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                                        <span>İlerleme</span>
                                        <span>{Math.round(getProgressPercentage(shipmentData.status))}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3">
                                        <div 
                                            className="bg-gradient-to-r from-brand-green to-brand-green-light h-3 rounded-full transition-all duration-500"
                                            style={{ width: `${getProgressPercentage(shipmentData.status)}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Kargo Firması */}
                                {shipmentData.shipping_provider && (
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-center">
                                            <TruckIcon className="h-5 w-5 text-gray-600 mr-2" />
                                            <span className="font-medium text-gray-900">
                                                Kargo Firması: {shipmentData.shipping_provider}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Teslimat Bilgileri */}
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                <MapPinIcon className="h-6 w-6 mr-2 text-brand-green" />
                                Teslimat Bilgileri
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Alıcı</h4>
                                    <p className="text-gray-600">{shipmentData.recipient_name}</p>
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Teslimat Adresi</h4>
                                    <p className="text-gray-600">{shipmentData.delivery_address}</p>
                                </div>
                            </div>
                        </div>

                        {/* Kargo Detayları */}
                        {shipmentData.shipment_details && (
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                    <ClockIcon className="h-6 w-6 mr-2 text-brand-green" />
                                    Kargo Detayları
                                </h3>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <pre className="text-sm text-gray-600 whitespace-pre-wrap">
                                        {JSON.stringify(shipmentData.shipment_details, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}

                        {/* Geri Dön Butonu */}
                        <div className="text-center">
                            <button
                                onClick={() => navigate('/')}
                                className="inline-flex items-center px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors"
                            >
                                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                Ana Sayfaya Dön
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TrackingPage;